import { UserPreferences } from './user-preferences/UserPreferences';

const FALLBACK_LOCALE = 'en-US';

/** The locale of the UI copy itself. Product text is English-only, so a month
 *  or weekday name inlined INTO a sentence must be formatted with this instead
 *  of the user's regional locale — otherwise "Decide in September" renders as
 *  "Decide in сентябрь". Standalone dates stay locale-aware, as they should. */
export const COPY_LOCALE = FALLBACK_LOCALE;

/** Optional overrides for formatting functions (e.g. live preview before saving settings). */
export interface FormatOverrides {
  locale?: string;
  timezone?: string;
  timeFormatId?: number;
}

/** Returns the user's preferred BCP 47 locale for number/date formatting.
 *  Priority: override → UserPreferences (backend-synced) → browser locale → 'en-US' */
export function getFormatLocale(override?: string): string {
  if (override) return override;
  const stored = UserPreferences.getAll().regional.locale?.id;
  if (stored) return String(stored);
  if (typeof navigator !== 'undefined' && navigator.language) return navigator.language;
  return FALLBACK_LOCALE;
}

/**
 * @deprecated Use `getFormatLocale()` instead. Kept for backwards compatibility.
 */
export const NUMBER_FORMAT_LOCALE = FALLBACK_LOCALE;

export const NUMBER_FORMAT_FULL_OPTIONS: Intl.NumberFormatOptions = {
  localeMatcher: 'best fit',
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
};

export const NUMBER_FORMAT_INTEGER_OPTIONS: Intl.NumberFormatOptions = {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
};

export const NUMBER_FORMAT_COMPACT_OPTIONS: Intl.NumberFormatOptions = {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
};

const COMPACT_THRESHOLD = 1_000;
const CACHE_MAX_SIZE = 128;
const formatterCache = new Map<string, Intl.NumberFormat>();

function getCachedFormatter(
  key: string,
  factory: (locale: string) => Intl.NumberFormat,
  localeOverride?: string,
): Intl.NumberFormat {
  const locale = getFormatLocale(localeOverride);
  const fullKey = `${locale}:${key}`;
  let f = formatterCache.get(fullKey);
  if (!f) {
    if (formatterCache.size >= CACHE_MAX_SIZE) formatterCache.clear();
    f = factory(locale);
    formatterCache.set(fullKey, f);
  }
  return f;
}

function getFullFormatter(localeOverride?: string): Intl.NumberFormat {
  return getCachedFormatter(
    'full',
    (locale) => new Intl.NumberFormat(locale, NUMBER_FORMAT_FULL_OPTIONS),
    localeOverride,
  );
}

function getIntegerFormatter(localeOverride?: string): Intl.NumberFormat {
  return getCachedFormatter(
    'int',
    (locale) => new Intl.NumberFormat(locale, NUMBER_FORMAT_INTEGER_OPTIONS),
    localeOverride,
  );
}

function getCompactFormatter(localeOverride?: string): Intl.NumberFormat {
  return getCachedFormatter(
    'compact',
    (locale) => new Intl.NumberFormat(locale, NUMBER_FORMAT_COMPACT_OPTIONS),
    localeOverride,
  );
}

export function formatFullNumber(
  value: number | string,
  options?: { isRound?: boolean; locale?: string },
): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  if (num === 0) return '0';
  const formatter = options?.isRound
    ? getIntegerFormatter(options?.locale)
    : getFullFormatter(options?.locale);
  return formatter.format(num);
}

export function formatMetricNumber(value: number | string, localeOverride?: string): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  if (num === 0) return '0';
  if (num >= COMPACT_THRESHOLD) {
    return getCompactFormatter(localeOverride).format(num);
  }
  return getIntegerFormatter(localeOverride).format(num);
}

export function formatMetricNumberPlus(value: number | string, localeOverride?: string): string {
  const num = Number(value);
  if (!Number.isFinite(num) || num < COMPACT_THRESHOLD) return String(num);
  return `${getCompactFormatter(localeOverride).format(num)}+`;
}

export const NUMBER_FORMAT_PERCENT_OPTIONS: Intl.NumberFormatOptions = {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
};

/** Value in 0–1 range; Intl adds % and multiplies by 100 */
export function formatPercent(
  value: number | string | null | undefined,
  fractionDigits = 2,
  localeOverride?: string,
): string {
  const num = Number(value);
  const safe = Number.isFinite(num) ? num : 0;
  const key = `pct-${fractionDigits}`;
  const formatter = getCachedFormatter(
    key,
    (locale) =>
      new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }),
    localeOverride,
  );
  return formatter.format(safe);
}

export const CURRENCY_FORMAT_DEFAULT_OPTIONS: Intl.NumberFormatOptions = {
  style: 'currency',
  currencyDisplay: 'narrowSymbol',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

export function formatCurrencyAmount(
  amount: number | string,
  currencyCode = 'USD',
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number; locale?: string },
): string {
  const num = Number(amount);
  const safe = Number.isFinite(num) ? num : 0;
  const min = options?.minimumFractionDigits ?? 2;
  const max = options?.maximumFractionDigits ?? 2;
  const currency = (currencyCode || 'USD').toUpperCase();
  const key = `cur-${currency}-${min}-${max}`;
  const formatter = getCachedFormatter(
    key,
    (locale) =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        // "$500", never "US$500" — the plain sign people write, in any locale
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: min,
        maximumFractionDigits: max,
      }),
    options?.locale,
  );
  return formatter.format(safe);
}

export function getCurrencySymbol(currencyCode?: string, localeOverride?: string): string {
  const currency = (currencyCode || 'USD').toUpperCase();
  const key = `sym-${currency}`;
  const formatter = getCachedFormatter(
    key,
    (locale) =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    localeOverride,
  );
  const parts = formatter.formatToParts(0);
  return parts.find((p) => p.type === 'currency')?.value ?? '$';
}

export function formatByCurrency(
  amount: number | string,
  currency?: string,
  minimumFractionDigits = 2,
  localeOverride?: string,
): string {
  return formatCurrencyAmount(amount, currency || 'USD', {
    minimumFractionDigits,
    maximumFractionDigits: minimumFractionDigits,
    locale: localeOverride,
  });
}

export function formatCurrency(
  input: string,
  currency?: string,
  fractionalDigits = 2,
  localeOverride?: string,
): string {
  const num = Number(input);
  if (!Number.isFinite(num)) {
    throw new Error('Invalid input. Please provide a valid number string.');
  }
  return formatCurrencyAmount(num, currency || 'USD', {
    minimumFractionDigits: fractionalDigits,
    maximumFractionDigits: fractionalDigits,
    locale: localeOverride,
  });
}
